import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { Paginated, Resource } from '@/lib/api/types'

export type AnnouncementKind = 'service' | 'product_news'
export type AnnouncementStatus = 'draft' | 'sending' | 'sent'

export interface Announcement {
  id: string
  kind: AnnouncementKind
  subject: string
  preheader: string | null
  body: string
  button_label: string | null
  button_url: string | null
  status: AnnouncementStatus
  recipients: number
  in_app_recipients: number
  email_delivery: boolean
  published_at: string | null
  /** How the copies went, once it's sending. */
  progress: Record<'sent' | 'queued' | 'skipped' | 'failed', number> | null
  /** Opens and clicks, out of the emails Amazon SES tracked (null: not sent yet). */
  engagement?: { tracked: number; opened: number; clicked: number } | null
  updated_by: string | null
  sent_by: string | null
  sent_at: string | null
  /** A draft an owner set to publish at this time, and who. */
  scheduled_at?: string | null
  scheduled_by?: string | null
  updated_at: string | null
}

export interface AnnouncementList {
  data: Announcement[]
  meta: {
    kinds: Record<AnnouncementKind, { label: string; description: string }>
    in_app_audience: number
    merge_tags: Record<string, string>
  }
}

/** In-app only since 2026-10-23: emails to customers go through Marketing. */
export type AnnouncementInput = Pick<
  Announcement,
  'kind' | 'subject' | 'preheader' | 'body' | 'button_label' | 'button_url'
>

export function useAnnouncements() {
  return useQuery({
    queryKey: ['announcements'],
    queryFn: () => http.get<AnnouncementList>('/announcements'),
  })
}

export function useAnnouncement(id: string | undefined) {
  return useQuery({
    queryKey: ['announcements', id],
    enabled: id !== undefined,
    queryFn: async () => (await http.get<Resource<Announcement>>(`/announcements/${id ?? ''}`)).data,
    // While it goes out, its progress updates by itself.
    refetchInterval: (query) => (query.state.data?.status === 'sending' ? 3000 : false),
  })
}

export function useSaveAnnouncement(id: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: AnnouncementInput) =>
      id === undefined
        ? (await http.post<Resource<Announcement>>('/announcements', input)).data
        : (await http.patch<Resource<Announcement>>(`/announcements/${id}`, input)).data,
    onSuccess: (a) => {
      queryClient.setQueryData(['announcements', a.id], a)
      void queryClient.invalidateQueries({ queryKey: ['announcements'], exact: true })
    },
  })
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => http.delete(`/announcements/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['announcements'] }),
  })
}

export function useSendAnnouncement(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (recipients: number) =>
      (
        await http.post<Resource<Announcement>>(`/announcements/${id}/send`, {
          confirm_recipients: recipients,
        })
      ).data,
    onSuccess: (a) => {
      queryClient.setQueryData(['announcements', a.id], a)
      void queryClient.invalidateQueries({ queryKey: ['announcements'], exact: true })
    },
  })
}

/** Publishes it later: owners only; it stays a draft until then. */
export function useScheduleAnnouncement(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (sendAt: string) =>
      (await http.post<Resource<Announcement>>(`/announcements/${id}/schedule`, { send_at: sendAt })).data,
    onSuccess: (a) => {
      queryClient.setQueryData(['announcements', a.id], a)
      void queryClient.invalidateQueries({ queryKey: ['announcements'], exact: true })
    },
  })
}

export function useUnscheduleAnnouncement(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => (await http.delete<Resource<Announcement>>(`/announcements/${id}/schedule`)).data,
    onSuccess: (a) => {
      queryClient.setQueryData(['announcements', a.id], a)
      void queryClient.invalidateQueries({ queryKey: ['announcements'], exact: true })
    },
  })
}

type Content = Omit<AnnouncementInput, 'kind'> & { kind: AnnouncementKind }

/** How it will look in the app, with made-up details (rendered by the API, so it's exact). */
export function useAnnouncementPreview(content: Content, enabled: boolean) {
  return useQuery({
    queryKey: ['announcements', 'preview', content],
    enabled: enabled && content.subject.trim() !== '' && content.body.trim() !== '',
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await http.post<Resource<{ subject: string; html: string; app_html: string }>>(
          '/announcements/preview',
          content,
        )
      ).data,
  })
}

/** One person's copy of an announcement. */
export interface AnnouncementSend {
  id: number
  announcement: string | null
  announcement_id: string
  to: string | null
  status: 'queued' | 'sent' | 'skipped' | 'failed'
  reason: string | null
  queued_at: string | null
  sent_at: string | null
  /** First open and first click, from Amazon SES's tracking. */
  opened_at?: string | null
  clicked_at?: string | null
}

export type AnnouncementSendFilter = AnnouncementSend['status'] | 'all'

/** Copies of every announcement (or one), newest first, 25 a page. Refreshes while any wait. */
export function useAnnouncementSends(
  status: AnnouncementSendFilter,
  announcement: string,
  search: string,
  page: number,
) {
  return useQuery({
    queryKey: ['announcements', 'sends', status, announcement, search, page],
    queryFn: () =>
      http.get<Paginated<AnnouncementSend>>('/announcements/sends', {
        status: status === 'all' ? undefined : status,
        announcement,
        search,
        page,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.data.some((s) => s.status === 'queued') ? 4000 : false),
  })
}
